import React, { useState } from 'react';

import useHasEverBeenInView from '../hooks/useHasEverBeenInView';
import {
  useGetFromThumbnailCache,
  useAddToThumbnailCache,
} from '../hooks/useThumbnailCache';
import type { ThumbnailMetadata } from '../utils/videoUtils';

interface ThumbnailProps {
  src: string;
  thumbnail: ThumbnailMetadata;
}

export default function Thumbnail({ src = '', thumbnail }: ThumbnailProps) {
  const [thumbnailElement, setThumbnailElement] = useState<HTMLElement | null>(
    null
  );

  // Keep track of whether this thumbnail has ever been in view
  // We defer loading the thumbnail until it is viewable
  const hasEverBeenInView = useHasEverBeenInView(thumbnailElement, {
    // Ensure the filmstrip has been in view for 200ms so we don't kick off a
    // ton of network requests if they are scrolling quickly through the timeline
    forAtLeastMs: 200,
  });

  const containerStyle = {
    width: thumbnail.width,
    height: thumbnail.height,
  };

  // Render a cached image of the thumbnail if we have seen it before
  useAddToThumbnailCache(
    thumbnailElement as HTMLVideoElement,
    src,
    thumbnail.timeInSeconds,
    thumbnail.width,
    thumbnail.height
  );
  const cachedDataUrl = useGetFromThumbnailCache(
    src,
    thumbnail.timeInSeconds,
    thumbnail.width
  );

  if (cachedDataUrl) {
    return (
      <img
        ref={setThumbnailElement}
        className={`flex-shrink-0 object-cover`}
        style={containerStyle}
        src={cachedDataUrl}
        alt={`Thumbnail at ${thumbnail.timeInSeconds.toFixed(1)}s`}
      />
    );
  }

  // Avoid rendering a ton of videos all at once if many of them are off-screen
  // Once we have captured a filmstrip thumbnail we continue to render it for good UX
  if (!hasEverBeenInView) {
    return (
      <div
        ref={setThumbnailElement}
        className={`flex-shrink-0 bg-gray-800 flex items-center justify-center border border-gray-600`}
        style={containerStyle}
      >
        <div className="flex flex-col items-center gap-1">
          <div className="w-4 h-4 border border-gray-500 rounded bg-gray-700 flex items-center justify-center">
            <div className="w-2 h-2 bg-gray-400 rounded-sm"></div>
          </div>
          <span className="text-xs text-gray-400 font-mono">
            {Math.round(thumbnail.timeInSeconds)}s
          </span>
        </div>
      </div>
    );
  }

  // When this thumbnail is in view and is being rendered for the first time,
  // we render a video tag and seek to the specified frame
  return (
    <video
      ref={setThumbnailElement}
      className={`flex-shrink-0 object-cover`}
      style={containerStyle}
      src={src}
      crossOrigin="anonymous"
      muted
      playsInline
      onLoadedMetadata={(e) => {
        // This event is fired when metadata has been loaded but before any frames have necessarily been loaded
        // We use this opportunity to seek to the time of the frame in question
        e.currentTarget.currentTime = thumbnail.timeInSeconds;
      }}
      onError={() => {
        console.error('Error loading thumbnail', src, thumbnail);
      }}
    />
  );
}
